import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button } from "@material-ui/core";
import TicketsUserSelect from "../TicketsUserSelect";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";

const buildFilterStorageKey = (userId) => `flowch-talk:tickets-filters:${userId}`;

const parseStoredIds = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
};

const readStoredFilters = (userId) => {
  if (!userId) return {};

  try {
    const rawValue = localStorage.getItem(buildFilterStorageKey(userId));
    if (!rawValue) return {};

    const parsedValue = JSON.parse(rawValue);
    return {
      selectedQueueIds: parseStoredIds(parsedValue?.selectedQueueIds),
      selectedUserIds: parseStoredIds(parsedValue?.selectedUserIds),
      includeNoQueue:
        typeof parsedValue?.includeNoQueue === "boolean"
          ? parsedValue.includeNoQueue
          : undefined,
      includeUnassigned:
        typeof parsedValue?.includeUnassigned === "boolean"
          ? parsedValue.includeUnassigned
          : undefined,
    };
  } catch (_err) {
    return {};
  }
};

const saveStoredFilters = (userId, filters) => {
  if (!userId) return;

  try {
    localStorage.setItem(
      buildFilterStorageKey(userId),
      JSON.stringify({
        selectedQueueIds: parseStoredIds(filters.selectedQueueIds),
        selectedUserIds: parseStoredIds(filters.selectedUserIds),
        includeNoQueue: Boolean(filters.includeNoQueue),
        includeUnassigned: Boolean(filters.includeUnassigned),
      })
    );
  } catch (_err) {
    // Browser storage can be unavailable in private modes.
  }
};

const useStyles = makeStyles((theme) => ({
  managerRoot: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    border: "none",
    boxShadow: "none",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  ticketsWrapper: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  openPanelsWrapper: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
    overflow: "hidden",
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
  },
  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tab: {
    minWidth: 120,
    width: 120,
  },
  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
    overflowX: "hidden",
  },
  serachInputWrapper: {
    flex: 1,
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary, 
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    right: "-10px",
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
  actionButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    paddingLeft: 14,
    paddingRight: 14,
    minHeight: 36,
    width: "100%",
    justifyContent: "center",
    "&:hover": {
      boxShadow: "none",
    },
  },
  actionButtonSlot: {
    flex: 1,
    minWidth: 92,
    display: "flex",
    justifyContent: "stretch",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minWidth: 0,
  },
  userFilterSlot: {
    flex: "1 1 170px",
    minWidth: 110,
    maxWidth: 190,
    overflow: "hidden",
  },
  queueFilterSlot: {
    flex: "1 1 130px",
    minWidth: 96,
    maxWidth: 150,
    overflow: "hidden",
  },
  actionButtonIcon: {
    marginRight: 6,
  },
  actionButtonLabel: {
    whiteSpace: "nowrap",
  },
  ["@media (max-width:420px)"]: {
    actionButtonSlot: {
      justifyContent: "center",
      flex: "0 0 46px",
      minWidth: 46,
    },
    actionButton: {
      width: 46,
      minWidth: 46,
      height: 46,
      minHeight: 46,
      borderRadius: "50%",
      paddingLeft: 0,
      paddingRight: 0,
    },
    actionButtonIcon: {
      marginRight: 0,
    },
    actionButtonLabel: {
      display: "none",
    },
    userFilterSlot: {
      flex: "1 1 150px",
      minWidth: 105,
      maxWidth: "none",
    },
    queueFilterSlot: {
      flex: "1 1 110px",
      minWidth: 90,
      maxWidth: "none",
    },
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const location = useLocation();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const userQueueIds = useMemo(() => user.queues.map((q) => q.id), [user.queues]);
  const storedFilters = readStoredFilters(user?.id);
  const storedQueueIds = parseStoredIds(storedFilters.selectedQueueIds).filter(
    (queueId) => userQueueIds.includes(queueId)
  );
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    storedQueueIds.length ? storedQueueIds : userQueueIds || []
  );
  const [includeNoQueue, setIncludeNoQueue] = useState(
    typeof storedFilters.includeNoQueue === "boolean"
      ? storedFilters.includeNoQueue
      : true
  );
  const [includeUnassigned, setIncludeUnassigned] = useState(
    typeof storedFilters.includeUnassigned === "boolean"
      ? storedFilters.includeUnassigned
      : true
  );
  const canShowAllTickets = user?.profile?.toUpperCase() === "ADMIN";
  const availableUserIds = useMemo(
    () =>
      availableUsers
        .map((loadedUser) => Number(loadedUser.id))
        .filter((availableUserId) => Number.isFinite(availableUserId)),
    [availableUsers]
  );
  const userFilterMode = useMemo(() => {
    if (!canShowAllTickets) {
      return "all";
    }

    if (selectedUserIds.length === 0 && !includeUnassigned) {
      return availableUserIds.length === 0 ? "all" : "none";
    }

    const selectedUserSet = new Set(
      selectedUserIds.map((selectedUserId) => Number(selectedUserId))
    );
    const hasAllAvailableUsers =
      availableUserIds.length > 0 &&
      availableUserIds.every((availableUserId) =>
        selectedUserSet.has(availableUserId)
      );

    return hasAllAvailableUsers && includeUnassigned ? "all" : "custom";
  }, [availableUserIds, canShowAllTickets, includeUnassigned, selectedUserIds]);
  const queueFilterMode = useMemo(() => {
    if (selectedQueueIds.length === 0 && !includeNoQueue) {
      return "none";
    }

    const selectedQueueSet = new Set(
      selectedQueueIds.map((selectedQueueId) => Number(selectedQueueId))
    );
    const hasAllAvailableQueues =
      userQueueIds.length > 0 &&
      userQueueIds.every((queueId) => selectedQueueSet.has(Number(queueId)));

    return hasAllAvailableQueues && includeNoQueue ? "all" : "custom";
  }, [includeNoQueue, selectedQueueIds, userQueueIds]);

  useEffect(() => {
    const validQueueIds = selectedQueueIds.filter((queueId) =>
      userQueueIds.includes(Number(queueId))
    );

    if (validQueueIds.length !== selectedQueueIds.length) {
      setSelectedQueueIds(validQueueIds);
      return;
    }

    if (canShowAllTickets && availableUsers.length === 0) {
      return;
    }

    saveStoredFilters(user?.id, {
      selectedQueueIds,
      selectedUserIds,
      includeNoQueue,
      includeUnassigned,
    });
  }, [
    availableUsers.length,
    canShowAllTickets,
    includeNoQueue,
    includeUnassigned,
    selectedQueueIds,
    selectedUserIds,
    user?.id,
    userQueueIds,
  ]);

  useEffect(() => {
    if (canShowAllTickets) {
      setShowAllTickets(true);
      const fetchUsers = async () => {
        try {
          const users = [];
          let pageNumber = 1;
          let hasMore = true;

          while (hasMore) {
            const { data } = await api.get("/users/", {
              params: {
                searchParam: "",
                pageNumber,
              },
            });

            const usersBatch = Array.isArray(data?.users) ? data.users : [];
            users.push(...usersBatch);

            hasMore = Boolean(data?.hasMore) && usersBatch.length > 0;
            pageNumber += 1;
          }

          setAvailableUsers(users);
          const storedUserIds = parseStoredIds(
            readStoredFilters(user?.id).selectedUserIds
          );
          const availableUserIds = users.map((loadedUser) => Number(loadedUser.id));
          const validStoredUserIds = storedUserIds.filter((storedUserId) =>
            availableUserIds.includes(storedUserId)
          );

          setSelectedUserIds(
            validStoredUserIds.length ? validStoredUserIds : availableUserIds
          );
        } catch (err) {
          toastError(err);
          setAvailableUsers([]);
          setSelectedUserIds([]);
        }
      };

      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowAllTickets]);

  useEffect(() => {
    if (!canShowAllTickets) {
      return undefined;
    }

    const socket = openSocket();

    socket.on("userPresence", (data) => {
      const presenceUserId = Number(data?.userId);
      const isOnline = Boolean(data?.isOnline);

      setAvailableUsers((prevUsers) =>
        prevUsers.map((loadedUser) =>
          Number(loadedUser.id) === presenceUserId
            ? { ...loadedUser, isOnline }
            : loadedUser
        )
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [canShowAllTickets]);

  useEffect(() => {
    if (!location.state) {
      return;
    }

    if (location.state.switchTab === "open") {
      setTab("open");
      setTabOpen("open");
    }

    if (location.state.switchTab === "pending") {
      setTab("open");
      setTabOpen("pending");
    }

    if (location.state.switchTab === "closed") {
      setTab("closed");
    }

    if (location.state.openNewTicket) {
      setNewTicketModalOpen(true);
    }

    // clear the state so it doesn't re-trigger
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
      setSearchParam("");
    }
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  return (
    <Paper elevation={0} className={classes.managerRoot}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
        >
          <Tab
            value={"open"}
            icon={<MoveToInboxIcon />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"closed"}
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        {tab === "search" ? (
          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        ) : (
          <div className={classes.controlsRow}>
            <div className={classes.actionButtonSlot}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={classes.actionButton}
                onClick={() => setNewTicketModalOpen(true)}
              >
                <AddIcon className={classes.actionButtonIcon} />
                <span className={classes.actionButtonLabel}>
                  {i18n.t("ticketsManager.buttons.newTicket")}
                </span>
              </Button>
            </div>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <div className={classes.userFilterSlot}>
                  <TicketsUserSelect
                    users={availableUsers}
                    selectedUserIds={selectedUserIds}
                    includeUnassigned={includeUnassigned}
                    onChange={(values) =>
                      setSelectedUserIds(
                        (values || [])
                          .map((userId) => Number(userId))
                          .filter((userId) => Number.isFinite(userId))
                      )
                    }
                    onSelectOnly={(selectedUserId) =>
                      setSelectedUserIds((currentUserIds) => {
                        const numericUserId = Number(selectedUserId);
                        const allUserIds = availableUsers
                          .map((loadedUser) => Number(loadedUser.id))
                          .filter((userId) => Number.isFinite(userId));

                        if (
                          currentUserIds.length === 1 &&
                          currentUserIds[0] === numericUserId
                        ) {
                          setIncludeUnassigned(true);
                          return allUserIds;
                        }

                        setIncludeUnassigned(false);
                        return [numericUserId];
                      })
                    }
                    onSelectUnassignedOnly={() => {
                      setSelectedUserIds([]);
                      setIncludeUnassigned(true);
                    }}
                    onToggleUnassigned={() =>
                      setIncludeUnassigned((currentValue) => !currentValue)
                    }
                    onSelectAll={() => {
                      setSelectedUserIds(
                        availableUsers
                          .map((loadedUser) => Number(loadedUser.id))
                          .filter((userId) => Number.isFinite(userId))
                      );
                      setIncludeUnassigned(true);
                    }}
                    onClearAll={() => {
                      setSelectedUserIds([]);
                      setIncludeUnassigned(false);
                    }}
                  />
                </div>
              )}
            />
            <div className={classes.queueFilterSlot}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                includeNoQueue={includeNoQueue}
                onChange={(values) => setSelectedQueueIds(values)}
                onSelectOnly={(queueId) =>
                  setSelectedQueueIds((currentQueueIds) => {
                    const numericQueueId = Number(queueId);
                    if (
                      currentQueueIds.length === 1 &&
                      currentQueueIds[0] === numericQueueId
                    ) {
                      setIncludeNoQueue(true);
                      return userQueueIds;
                    }

                    setIncludeNoQueue(false);
                    return [numericQueueId];
                  })
                }
                onSelectNoQueueOnly={() => {
                  setSelectedQueueIds([]);
                  setIncludeNoQueue(true);
                }}
                onToggleNoQueue={() =>
                  setIncludeNoQueue((currentValue) => !currentValue)
                }
                onSelectAll={() => {
                  setSelectedQueueIds(userQueueIds);
                  setIncludeNoQueue(true);
                }}
                onClearAll={() => {
                  setSelectedQueueIds([]);
                  setIncludeNoQueue(false);
                }}
              />
            </div>
          </div>
        )}
      </Paper>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={openCount}
                color="primary"
              >
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value={"open"}
          />
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={pendingCount}
                color="secondary"
              >
                {i18n.t("ticketsList.pendingHeader")}
              </Badge>
            }
            value={"pending"}
          />
        </Tabs>
        <Paper className={classes.openPanelsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedUserIds={selectedUserIds}
            queueFilterMode={queueFilterMode}
            userFilterMode={userFilterMode}
            includeNoQueue={includeNoQueue}
            includeUnassigned={includeUnassigned}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsList
            status="pending"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedUserIds={selectedUserIds}
            queueFilterMode={queueFilterMode}
            userFilterMode={userFilterMode}
            includeNoQueue={includeNoQueue}
            includeUnassigned={includeUnassigned}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedUserIds={selectedUserIds}
          queueFilterMode={queueFilterMode}
          userFilterMode={userFilterMode}
          includeNoQueue={includeNoQueue}
          includeUnassigned={includeUnassigned}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedUserIds={selectedUserIds}
          queueFilterMode={queueFilterMode}
          userFilterMode={userFilterMode}
          includeNoQueue={includeNoQueue}
          includeUnassigned={includeUnassigned}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
